import { tmpPrivate, tmpPublic } from '~/amplify';
import { v4 as uuidv4 } from 'uuid';
import type { UploadDataWithPathInput } from 'aws-amplify/storage';
import type { UploadFileType } from '../..';
import { assertNever } from '~/util/assertNever';

export const FILE_SIZE_LIMIT = 10000000; // 10MB in bytes

export const validImageTypes = [
  'image/gif',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/svg+xml',
  'image/webp',
] as const;

export enum ImageUploadError {
  InvalidFileType = 'InvalidFileType',
  InvalidFileSize = 'InvalidFileSize',
}

type Args = {
  uploadArgs: UploadFileType;
  identityId: string;
};

type ReturnType = {
  dataInput: UploadDataWithPathInput;
  processedFileName: string;
};

const getUploadDataInput = ({ uploadArgs, identityId }: Args): ReturnType => {
  const { fileName, file, level } = uploadArgs;
  const processedFileName = fileName || uuidv4();
  const type = file.type;
  const protectionLevel = level === 'public' ? tmpPublic : tmpPrivate;

  const basePath = `${protectionLevel}${identityId}/`;

  switch (uploadArgs.type) {
    case 'PluginsEditorImage':
    case 'File': {
      return {
        dataInput: {
          data: file,
          options: { contentType: type },
          path: `${basePath}${uploadArgs.userId}/${processedFileName}`,
        },
        processedFileName,
      };
    }
    case 'Image': {
      const fileType = file.type;
      if (!validImageTypes.includes(fileType as any)) {
        throw new Error(`${ImageUploadError.InvalidFileType}:${fileType}`);
      }

      if (file.size > FILE_SIZE_LIMIT) {
        throw new Error(`${ImageUploadError.InvalidFileSize}:${file.size}`);
      }

      return {
        dataInput: {
          data: file,
          options: { contentType: type },
          path: `${basePath}${processedFileName}`,
        },
        processedFileName,
      };
    }
    default:
      return assertNever(uploadArgs);
  }
};

export default getUploadDataInput;
